import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Bell, CheckCircle2, MessageCircle, Phone, RefreshCw, Sparkles, X } from 'lucide-react';
import ConversationThread from './ConversationThread';
import {
  createOrGetSupportConversation,
  fetchAssistantTipNext,
  fetchSupportUnreadCount,
  recordAssistantTipEvent,
} from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';
import { getAlertDescription, getAlertLabel } from '../../delivery-alerts/alertTypes';

const API = process.env.REACT_APP_API_URL || '';
const BUTTON_WIDTH = 64;
const BUTTON_HEIGHT = 64;
const BUTTON_MARGIN = 16;
const ALERT_STACK_OFFSET = 76;
const POLL_INTERVAL_MS = 30 * 1000;
const ALERTS_POLL_INTERVAL_MS = 60 * 1000;
const ALERT_PANEL_GAP = 12;
const ALERT_PANEL_WIDTH = 384;
const ASSISTANT_TIP_SURFACE = 'direct_support_floating_chat';
// Test timing: show quickly, retry often, and auto-hide after a few seconds.
const ASSISTANT_TIP_INITIAL_DELAY_MS = 25 * 1000;
const ASSISTANT_TIP_REPEAT_MS = 1 * 60 * 1000;
const ASSISTANT_TIP_VISIBLE_MS = 10 * 1000;
const ASSISTANT_TIP_WIDTH = 286;
const ASSISTANT_TIP_HEIGHT = 156;

function getViewportSize() {
  if (typeof window === 'undefined') return { width: 1024, height: 768 };
  return { width: window.innerWidth, height: window.innerHeight };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function supportScopeForRole(role) {
  if (role === 'driver') return 'driver';
  if (role === 'provider') return 'provider';
  if (role === 'customer') return 'store';
  return null;
}

function defaultPosition(viewport = getViewportSize()) {
  return {
    x: Math.max(BUTTON_MARGIN, viewport.width - BUTTON_WIDTH - BUTTON_MARGIN),
    y: Math.max(BUTTON_MARGIN, viewport.height - BUTTON_HEIGHT - BUTTON_MARGIN - ALERT_STACK_OFFSET),
  };
}

function clampPosition(position, viewport = getViewportSize()) {
  return {
    x: clamp(position.x, BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.width - BUTTON_WIDTH - BUTTON_MARGIN)),
    y: clamp(position.y, BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.height - BUTTON_HEIGHT - BUTTON_MARGIN)),
  };
}

function readStoredPosition(storageKey) {
  if (typeof window === 'undefined') return defaultPosition();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return defaultPosition();
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return defaultPosition();
    return clampPosition(parsed);
  } catch (_) {
    return defaultPosition();
  }
}

function compactError(err, fallback) {
  const payload = err?.response?.data;
  return payload?.message || payload?.error || err?.message || fallback;
}

function authConfig(extra = {}) {
  const token = window.localStorage.getItem('token');
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

function severityClasses(severity) {
  if (severity === 'high') {
    return {
      badge: 'bg-red-100 text-red-700',
      border: 'border-red-300',
      icon: 'text-red-600 bg-red-50',
      bell: 'bg-red-600 text-white ring-red-200',
      dot: 'bg-red-500',
    };
  }
  if (severity === 'low') {
    return {
      badge: 'bg-slate-100 text-slate-700',
      border: 'border-slate-300',
      icon: 'text-slate-600 bg-slate-50',
      bell: 'bg-slate-700 text-white ring-slate-200',
      dot: 'bg-slate-400',
    };
  }
  return {
    badge: 'bg-amber-100 text-amber-700',
    border: 'border-amber-300',
    icon: 'text-amber-600 bg-amber-50',
    bell: 'bg-amber-500 text-white ring-amber-200',
    dot: 'bg-amber-500',
  };
}

function highestAlertSeverity(alerts = []) {
  if (alerts.some(alert => alert.severity === 'high')) return 'high';
  if (alerts.some(alert => alert.severity === 'medium')) return 'medium';
  if (alerts.some(alert => alert.severity === 'low')) return 'low';
  return null;
}

function formatAlertTime(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('es-PE', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    }).format(new Date(value));
  } catch (_) {
    return '';
  }
}

function upsertAlert(alerts, incoming) {
  const active = incoming.status === 'open' || incoming.status === 'acknowledged';
  if (!active) return alerts.filter(alert => alert.id !== incoming.id);

  const exists = alerts.some(alert => alert.id === incoming.id);
  if (exists) return alerts.map(alert => alert.id === incoming.id ? incoming : alert);
  return [incoming, ...alerts];
}

function AlertDetailModal({ alert, role, onClose, onAction, updating }) {
  if (!alert) return null;

  const delivery = alert.delivery || {};
  const classes = severityClasses(alert.severity);

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Cerrar" />
      <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="flex items-start gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${classes.icon}`}>
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{getAlertLabel(alert.alert_type)}</h2>
              <p className="text-xs text-slate-500">{formatAlertTime(alert.created_at)}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          <div className="flex flex-wrap gap-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${classes.badge}`}>
              {alert.severity === 'high' ? 'Alta' : alert.severity === 'low' ? 'Baja' : 'Media'}
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
              {alert.status === 'acknowledged' ? 'En atencion' : 'Nueva'}
            </span>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm text-slate-700">{getAlertDescription(alert.alert_type)}</p>
            {alert.message && (
              <p className="mt-3 text-sm font-medium text-slate-900">{alert.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <div className="text-xs text-slate-500">Entrega</div>
              <div className="font-semibold text-slate-900">
                #{delivery.id || alert.delivery_point_id}
                {(role === 'admin' || role === 'master') && delivery.sequence != null ? ` · Secuencia ${delivery.sequence}` : ''}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Tienda</div>
              <div className="font-semibold text-slate-900">{delivery.user_name || alert.user?.username || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Cliente final</div>
              <div className="font-semibold text-slate-900">{delivery.recipient_name || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Conductor</div>
              <div className="font-semibold text-slate-900">{alert.driver?.username || '-'}</div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-xs text-slate-500">Direccion</div>
            <div className="mt-1 text-sm font-medium text-slate-900">
              {[delivery.address, delivery.district].filter(Boolean).join(', ') || '-'}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {delivery.recipient_phone && (
              <a
                href={`tel:${delivery.recipient_phone}`}
                className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Phone className="h-4 w-4" />
                Cliente
              </a>
            )}
            {alert.driver?.phone_number && (
              <a
                href={`tel:${alert.driver.phone_number}`}
                className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Phone className="h-4 w-4" />
                Conductor
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 border-t border-slate-200 p-5 sm:grid-cols-3">
          <button
            onClick={() => onAction(alert, 'acknowledge')}
            disabled={updating || alert.status === 'acknowledged'}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Atender
          </button>
          <button
            onClick={() => onAction(alert, 'dismiss')}
            disabled={updating}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Descartar
          </button>
          <button
            onClick={() => onAction(alert, 'resolve')}
            disabled={updating}
            className="rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary/90 disabled:opacity-50"
          >
            Resolver
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DirectSupportFloatingChat({ role, userId, providerId }) {
  const supportScope = supportScopeForRole(role);
  const supportEnabled = Boolean(supportScope && userId);
  const alertsEnabled = Boolean(userId && ['master', 'admin', 'provider', 'customer'].includes(role));
  const enabled = supportEnabled || alertsEnabled;
  const storageKey = `fastMaxDirectSupportButtonPosition:${role || 'unknown'}:${userId || 'anon'}`;
  const [viewport, setViewport] = useState(getViewportSize);
  const [buttonPosition, setButtonPosition] = useState(() => readStoredPosition(storageKey));
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTip, setActiveTip] = useState(null);
  const [tipVisible, setTipVisible] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertPanelOpen, setAlertPanelOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertUpdating, setAlertUpdating] = useState(false);
  const dragStateRef = useRef(null);
  const didDragRef = useRef(false);
  const unreadFetchRef = useRef(false);
  const alertFetchRef = useRef(false);
  const tipFetchRef = useRef(false);
  const tipLoopTimerRef = useRef(null);
  const tipHideTimerRef = useRef(null);
  const openRef = useRef(open);
  const unreadCountRef = useRef(unreadCount);
  const tipVisibleRef = useRef(tipVisible);

  const roomPayload = useMemo(() => socketAuthPayload({
    scope: supportScope,
    user_id: userId,
  }), [supportScope, userId]);

  const normalizedProviderId = providerId && providerId !== 'null' && providerId !== 'undefined'
    ? providerId
    : null;

  const alertJoinPayload = useMemo(() => ({
    role,
    user_id: userId,
    provider_id: normalizedProviderId,
  }), [role, userId, normalizedProviderId]);

  const visibleAlerts = useMemo(() => alerts.slice(0, alertPanelOpen ? 8 : 3), [alertPanelOpen, alerts]);
  const alertSeverity = highestAlertSeverity(alerts);
  const alertButtonClasses = alertSeverity
    ? severityClasses(alertSeverity).bell
    : 'bg-emerald-600 text-white ring-emerald-200';

  const assistantTipLayout = useMemo(() => {
    const width = Math.min(ASSISTANT_TIP_WIDTH, Math.max(220, viewport.width - (BUTTON_MARGIN * 2)));
    const mobile = viewport.width < 520;
    const opensLeft = !mobile && buttonPosition.x > viewport.width / 2;
    const left = mobile
      ? clamp(buttonPosition.x + (BUTTON_WIDTH / 2) - (width / 2), BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.width - width - BUTTON_MARGIN))
      : opensLeft
        ? clamp(buttonPosition.x - width - 12, BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.width - width - BUTTON_MARGIN))
        : clamp(buttonPosition.x + BUTTON_WIDTH + 12, BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.width - width - BUTTON_MARGIN));
    const top = mobile
      ? clamp(buttonPosition.y - ASSISTANT_TIP_HEIGHT - 12, BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.height - ASSISTANT_TIP_HEIGHT - BUTTON_MARGIN))
      : clamp(buttonPosition.y - 18, BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.height - ASSISTANT_TIP_HEIGHT - BUTTON_MARGIN));
    return { left, top, width, mobile, opensLeft };
  }, [buttonPosition, viewport]);

  const alertPanelStyle = useMemo(() => {
    const panelWidth = Math.min(ALERT_PANEL_WIDTH, viewport.width - (BUTTON_MARGIN * 2));
    const panelLeft = clamp(
      buttonPosition.x + BUTTON_WIDTH - panelWidth,
      BUTTON_MARGIN,
      viewport.width - panelWidth - BUTTON_MARGIN,
    );
    const spaceAboveButton = buttonPosition.y - BUTTON_MARGIN - ALERT_PANEL_GAP;
    return spaceAboveButton >= 180
      ? {
        left: panelLeft,
        bottom: viewport.height - buttonPosition.y + ALERT_PANEL_GAP,
        width: panelWidth,
        maxHeight: Math.max(180, spaceAboveButton),
      }
      : {
        left: panelLeft,
        top: buttonPosition.y + BUTTON_HEIGHT + ALERT_PANEL_GAP,
        width: panelWidth,
        maxHeight: Math.max(180, viewport.height - buttonPosition.y - BUTTON_HEIGHT - BUTTON_MARGIN - ALERT_PANEL_GAP),
      };
  }, [buttonPosition, viewport]);

  const refreshUnread = useCallback(async () => {
    if (!supportEnabled || unreadFetchRef.current) return;
    try {
      unreadFetchRef.current = true;
      const data = await fetchSupportUnreadCount(supportScope, userId);
      setUnreadCount(Number(data?.unread_count || 0));
      if (data?.conversation_id) setConversationId(data.conversation_id);
    } catch (err) {
      console.error('Support unread count error:', err);
    } finally {
      unreadFetchRef.current = false;
    }
  }, [supportEnabled, supportScope, userId]);

  useEffect(() => {
    openRef.current = open;
    unreadCountRef.current = unreadCount;
    tipVisibleRef.current = tipVisible;
  }, [open, tipVisible, unreadCount]);

  const hideAssistantTip = useCallback(() => {
    tipVisibleRef.current = false;
    setTipVisible(false);
    if (tipHideTimerRef.current) {
      window.clearTimeout(tipHideTimerRef.current);
      tipHideTimerRef.current = null;
    }
  }, []);

  const showAssistantTip = useCallback(async () => {
    if (!supportEnabled || openRef.current || alertPanelOpen || unreadCountRef.current > 0 || tipFetchRef.current || tipVisibleRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

    tipFetchRef.current = true;
    try {
      const data = await fetchAssistantTipNext(ASSISTANT_TIP_SURFACE);
      const tip = data?.tip || null;
      if (!tip?.id) return;
      setActiveTip(tip);
      tipVisibleRef.current = true;
      setTipVisible(true);
      recordAssistantTipEvent(tip.id, 'shown', {
        surface: ASSISTANT_TIP_SURFACE,
        metadata: { source: 'direct_support_floating_chat' },
      }).catch(err => console.error('Assistant tip shown event error:', err));

      if (tipHideTimerRef.current) window.clearTimeout(tipHideTimerRef.current);
      tipHideTimerRef.current = window.setTimeout(() => {
        tipVisibleRef.current = false;
        setTipVisible(false);
        tipHideTimerRef.current = null;
      }, ASSISTANT_TIP_VISIBLE_MS);
    } catch (err) {
      // Assistant tips are non-critical. Support chat must continue working even if
      // the education/announcement endpoint is unavailable.
      console.error('Assistant tip fetch error:', err);
    } finally {
      tipFetchRef.current = false;
    }
  }, [alertPanelOpen, supportEnabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const handleResize = () => {
      const nextViewport = getViewportSize();
      setViewport(nextViewport);
      setButtonPosition(prev => clampPosition(prev, nextViewport));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(buttonPosition));
    } catch (_) {
      // localStorage can be unavailable in private browsing or locked-down contexts.
    }
  }, [buttonPosition, enabled, storageKey]);

  useEffect(() => {
    if (!supportEnabled) return undefined;
    refreshUnread();
    const intervalId = window.setInterval(refreshUnread, POLL_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshUnread();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [supportEnabled, refreshUnread]);

  useEffect(() => {
    if (!supportEnabled) return undefined;

    const scheduleNextTip = (delay) => {
      tipLoopTimerRef.current = window.setTimeout(async () => {
        await showAssistantTip();
        scheduleNextTip(ASSISTANT_TIP_REPEAT_MS);
      }, delay);
    };

    scheduleNextTip(ASSISTANT_TIP_INITIAL_DELAY_MS);
    return () => {
      if (tipLoopTimerRef.current) window.clearTimeout(tipLoopTimerRef.current);
      if (tipHideTimerRef.current) window.clearTimeout(tipHideTimerRef.current);
      tipLoopTimerRef.current = null;
      tipHideTimerRef.current = null;
    };
  }, [supportEnabled, showAssistantTip]);

  useEffect(() => {
    if (open || alertPanelOpen || unreadCount > 0) hideAssistantTip();
  }, [alertPanelOpen, hideAssistantTip, open, unreadCount]);

  useEffect(() => {
    if (!supportEnabled) return undefined;
    const joinRoom = () => socket.emit('join_communications_scope', roomPayload);
    const leaveRoom = () => socket.emit('leave_communications_scope', roomPayload);
    const refreshFromEvent = (payload) => {
      const nextConversation = payload?.conversation || null;
      if (!conversationId || Number(payload?.conversation_id || nextConversation?.id) === Number(conversationId)) {
        refreshUnread();
      }
    };
    const rejoin = () => {
      joinRoom();
      refreshUnread();
    };

    joinRoom();
    socket.on('connect', rejoin);
    socket.on('conversation_message_created', refreshFromEvent);
    socket.on('conversation_updated', refreshFromEvent);
    socket.on('conversation_read_updated', refreshFromEvent);
    return () => {
      socket.off('connect', rejoin);
      socket.off('conversation_message_created', refreshFromEvent);
      socket.off('conversation_updated', refreshFromEvent);
      socket.off('conversation_read_updated', refreshFromEvent);
      leaveRoom();
    };
  }, [conversationId, supportEnabled, refreshUnread, roomPayload]);

  const joinAlertRooms = useCallback(() => {
    if (!alertsEnabled || !userId) return;
    socket.emit('join_delivery_alerts', alertJoinPayload);
  }, [alertJoinPayload, alertsEnabled, userId]);

  const fetchAlerts = useCallback(async ({ force = false } = {}) => {
    if (!alertsEnabled || !userId || alertFetchRef.current) return;
    if (!force && typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

    try {
      alertFetchRef.current = true;
      const response = await axios.get(`${API}/api/delivery-alerts/open`, {
        params: { role, user_id: userId },
        ...authConfig(),
      });
      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('Error loading delivery alerts:', err);
    } finally {
      alertFetchRef.current = false;
    }
  }, [alertsEnabled, role, userId]);

  useEffect(() => {
    if (!alertsEnabled || !userId) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchAlerts({ force: true });
    };
    const handleCreated = (alert) => {
      setAlerts(prev => upsertAlert(prev, alert));
      setAlertPanelOpen(true);
      hideAssistantTip();
    };
    const handleUpdated = (alert) => {
      setAlerts(prev => upsertAlert(prev, alert));
      setSelectedAlert(prev => prev?.id === alert.id ? alert : prev);
    };
    const handleConnect = () => joinAlertRooms();

    fetchAlerts({ force: true });
    joinAlertRooms();
    const pollId = window.setInterval(fetchAlerts, ALERTS_POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    socket.on('delivery_alert_created', handleCreated);
    socket.on('delivery_alert_updated', handleUpdated);
    socket.on('connect', handleConnect);

    return () => {
      socket.off('delivery_alert_created', handleCreated);
      socket.off('delivery_alert_updated', handleUpdated);
      socket.off('connect', handleConnect);
      socket.emit('leave_delivery_alerts', alertJoinPayload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(pollId);
    };
  }, [alertJoinPayload, alertsEnabled, fetchAlerts, hideAssistantTip, joinAlertRooms, userId]);

  const handleAlertAction = useCallback(async (alertItem, action) => {
    try {
      setAlertUpdating(true);
      const response = await axios.patch(`${API}/api/delivery-alerts/${alertItem.id}`, {
        action,
        user_id: userId,
      }, authConfig());
      const updated = response.data.alert;
      setAlerts(prev => upsertAlert(prev, updated));
      if (action === 'dismiss' || action === 'resolve') {
        setSelectedAlert(null);
      } else {
        setSelectedAlert(updated);
      }
    } catch (err) {
      console.error('Error updating delivery alert:', err);
      alert('No se pudo actualizar la alerta.');
    } finally {
      setAlertUpdating(false);
    }
  }, [userId]);

  const openSupport = useCallback(async () => {
    if (!supportEnabled || loading) {
      if (!supportEnabled && alertsEnabled) setAlertPanelOpen(open => !open);
      return;
    }
    setAlertPanelOpen(false);
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const nextConversation = await createOrGetSupportConversation(supportScope, userId);
      setConversation(nextConversation);
      setConversationId(nextConversation?.id || null);
      window.setTimeout(refreshUnread, 900);
    } catch (err) {
      console.error('Direct support conversation error:', err);
      setError(compactError(err, 'No se pudo abrir Fast Max.'));
    } finally {
      setLoading(false);
    }
  }, [alertsEnabled, loading, refreshUnread, supportEnabled, supportScope, userId]);

  const handleConversationLoaded = useCallback((nextConversation) => {
    if (nextConversation?.id) {
      setConversation(nextConversation);
      setConversationId(nextConversation.id);
      window.setTimeout(refreshUnread, 900);
    }
  }, [refreshUnread]);

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    hideAssistantTip();
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      initialPosition: buttonPosition,
    };
    didDragRef.current = false;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) didDragRef.current = true;
    setButtonPosition(clampPosition({
      x: dragState.initialPosition.x + deltaX,
      y: dragState.initialPosition.y + deltaY,
    }, viewport));
  };

  const handlePointerUp = (event) => {
    if (dragStateRef.current?.pointerId === event.pointerId) {
      dragStateRef.current = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  };

  const handleButtonClick = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    openSupport();
  };

  const handleAlertButtonClick = (event) => {
    event.stopPropagation();
    if (!alertsEnabled) return;
    hideAssistantTip();
    setOpen(false);
    setAlertPanelOpen(open => !open);
  };

  const handleTipDismiss = (event) => {
    event.stopPropagation();
    const tip = activeTip;
    hideAssistantTip();
    if (tip?.id) {
      recordAssistantTipEvent(tip.id, 'dismissed', {
        surface: ASSISTANT_TIP_SURFACE,
        metadata: { source: 'direct_support_floating_chat' },
      }).catch(err => console.error('Assistant tip dismiss event error:', err));
    }
  };

  const handleTipClick = () => {
    const tip = activeTip;
    hideAssistantTip();
    if (tip?.id) {
      recordAssistantTipEvent(tip.id, 'clicked', {
        surface: ASSISTANT_TIP_SURFACE,
        metadata: { action: tip.cta_action || 'open_chat', source: 'direct_support_floating_chat' },
      }).catch(err => console.error('Assistant tip click event error:', err));
    }

    if (tip?.cta_action === 'none') return;
    if (tip?.cta_action === 'url_hash' && tip?.cta_payload?.hash) {
      window.location.hash = tip.cta_payload.hash;
      return;
    }
    openSupport();
  };

  const activeConversationId = conversation?.id || conversationId;

  if (!enabled) return null;

  return (
    <>
      {tipVisible && activeTip && !open && unreadCount === 0 && (
        <div
          role="button"
          tabIndex={0}
          onClick={handleTipClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleTipClick();
            }
          }}
          className="fixed z-[3995] cursor-pointer rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-2xl ring-1 ring-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(15,23,42,0.22)]"
          style={{ left: assistantTipLayout.left, top: assistantTipLayout.top, width: assistantTipLayout.width }}
        >
          <span
            className={`absolute h-3 w-3 rotate-45 border-slate-200 bg-white ${
              assistantTipLayout.mobile
                ? '-bottom-1 left-1/2 -translate-x-1/2 border-b border-r'
                : assistantTipLayout.opensLeft
                  ? '-right-1 top-12 border-r border-t'
                  : '-left-1 top-12 border-b border-l'
            }`}
          />
          <div className="relative">
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-start gap-2">
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-[#B1336E]/10 text-[#B1336E]">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-wide text-[#B1336E]">
                    Fast Max Assistant
                  </div>
                  <h3 className="line-clamp-2 text-sm font-black leading-snug text-slate-950">
                    {activeTip.title}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={handleTipDismiss}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                title="Ocultar"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-xs font-semibold leading-relaxed text-slate-600">
              {activeTip.body}
            </p>
            {activeTip.cta_label && activeTip.cta_action !== 'none' && (
              <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#B1336E]/10 px-2.5 py-1 text-[11px] font-black text-[#B1336E]">
                {activeTip.cta_label}
              </div>
            )}
          </div>
        </div>
      )}

      {alertPanelOpen && alertsEnabled && (
        <div
          className="fixed z-[4005] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-900/5"
          style={alertPanelStyle}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2">
              <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${alerts.length ? severityClasses(alertSeverity).icon : 'bg-emerald-50 text-emerald-600'}`}>
                {alerts.length ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-950">Alertas activas</h3>
                <p className="text-[11px] font-semibold text-slate-500">
                  {alerts.length ? `${alerts.length} incidencia${alerts.length === 1 ? '' : 's'} en seguimiento` : 'Operacion sin alertas activas'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAlertPanelOpen(false)}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              title="Cerrar alertas"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {visibleAlerts.length > 0 ? (
            <div className="space-y-2 p-3">
              {visibleAlerts.map((item) => {
                const classes = severityClasses(item.severity);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedAlert(item)}
                    className={`w-full rounded-xl border ${classes.border} bg-white p-3 text-left shadow-sm transition-colors hover:bg-slate-50`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${classes.icon}`}>
                        <AlertTriangle className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="truncate text-sm font-bold text-slate-900">
                            {getAlertLabel(item.alert_type)}
                          </div>
                          <div className="flex-shrink-0 text-[11px] text-slate-500">
                            {formatAlertTime(item.created_at)}
                          </div>
                        </div>
                        <div className="mt-1 truncate text-xs text-slate-600">
                          #{item.delivery_point_id}
                          {(role === 'admin' || role === 'master') && item.delivery?.sequence != null ? ` · Seq ${item.delivery.sequence}` : ''}
                          {' - '}
                          {item.delivery?.recipient_name || 'Entrega'}
                        </div>
                        {item.message && (
                          <div className="mt-1 truncate text-xs text-slate-800">{item.message}</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-2 p-4 text-sm font-semibold text-slate-600">
              <CheckCircle2 className="h-5 w-5 text-brand-green" />
              No hay alertas activas.
            </div>
          )}
        </div>
      )}

      <div
        className="fixed z-[3990] h-16 w-16"
        style={{ left: buttonPosition.x, top: buttonPosition.y }}
      >
        <button
          type="button"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onClick={handleButtonClick}
          className="flex h-16 w-16 touch-none select-none items-center justify-center rounded-2xl bg-gradient-to-br from-white via-white to-[#B1336E]/10 p-1.5 text-white shadow-2xl ring-2 ring-[#B1336E]/25 transition hover:-translate-y-0.5 hover:scale-105 hover:ring-[#B1336E]/45 cursor-grab active:cursor-grabbing active:scale-95"
          title={supportEnabled ? 'Fast Max' : 'Alertas Fast Max'}
          aria-label={supportEnabled ? 'Abrir soporte Fast Max' : 'Abrir alertas Fast Max'}
        >
          <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-white shadow-inner">
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo192.png`}
              alt=""
              aria-hidden="true"
              draggable="false"
              className="h-full w-full object-contain p-1"
            />
          </span>
          {supportEnabled && unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-black text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {alertsEnabled && (
          <button
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={handleAlertButtonClick}
            className={`absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full shadow-xl ring-2 transition hover:scale-105 ${alertButtonClasses}`}
            title="Alertas de entrega"
            aria-label="Abrir resumen de alertas"
          >
            {alerts.length > 0 ? <AlertTriangle className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
            {alerts.length > 0 && (
              <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-black text-white ring-2 ring-white">
                {alerts.length > 99 ? '99+' : alerts.length}
              </span>
            )}
          </button>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[4200] pointer-events-none">
          <div className="absolute inset-0 bg-slate-950/20 pointer-events-auto" onClick={() => setOpen(false)} />
          <div className="absolute bottom-3 right-3 top-3 flex w-[min(430px,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl pointer-events-auto sm:bottom-5 sm:right-5 sm:top-auto sm:h-[min(680px,calc(100vh-2.5rem))]">
            <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#B1336E]/10 text-[#B1336E]">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-sm font-black text-slate-950">Fast Max</h2>
                  <p className="text-[11px] font-semibold text-slate-500">Soporte directo con administradores</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
                title="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {error && (
              <div className="m-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </div>
            )}

            {loading && !activeConversationId ? (
              <div className="flex flex-1 items-center justify-center gap-2 text-sm font-semibold text-slate-500">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Abriendo Fast Max...
              </div>
            ) : (
              <ConversationThread
                conversationId={activeConversationId}
                currentUserId={userId}
                currentUserType={supportScope}
                className="min-h-0 flex-1 rounded-none border-0"
                allowNotes={false}
                inputPlaceholder="Escribe a Fast Max..."
                inputHelperText="Mensaje interno directo para administradores de Fast Max. No envía WhatsApp."
                onConversationLoaded={handleConversationLoaded}
              />
            )}
          </div>
        </div>
      )}

      <AlertDetailModal
        alert={selectedAlert}
        role={role}
        onClose={() => setSelectedAlert(null)}
        onAction={handleAlertAction}
        updating={alertUpdating}
      />
    </>
  );
}
